import { Controller } from "@hotwired/stimulus";

// In-page search and sort for the ancillary list on bookings#confirm_fare.
// Works purely on the rendered rows: hiding a row never touches its inputs, so
// a hidden selection is still submitted.
//
// Usage:
//   data-controller="ancillary-filter"                        on the ancillaries card body
//   data-ancillary-filter-target="query"                      on the search input
//   data-ancillary-filter-target="sort"                       on the sort <select>
//   data-ancillary-filter-target="row"                        on each service row, with
//     data-name="<lowercased searchable text>" data-price-cents="<integer>"
//   data-ancillary-filter-target="empty"                      on the "no matches" hint
export default class AncillaryFilterController extends Controller {
  static targets = ["query", "sort", "row", "empty"];

  connect() {
    // Remember the server order so "default" can restore it.
    this.rowTargets.forEach((row, index) => {
      const current = row;
      current.dataset.originalIndex = String(index);
    });
  }

  filter() {
    const query = this.queryTarget.value.trim().toLowerCase();
    let visible = 0;

    this.rowTargets.forEach((row) => {
      const matches = !query || (row.dataset.name || "").includes(query);
      row.classList.toggle("hidden", !matches);
      if (matches) visible += 1;
    });

    if (this.hasEmptyTarget)
      this.emptyTarget.classList.toggle("hidden", visible > 0);
  }

  sort() {
    const comparator = this.comparator(this.sortTarget.value);

    // Rows are sorted within their own list (one per leg and service type),
    // never moved between tabs.
    const lists = new Set(this.rowTargets.map((row) => row.parentElement));
    lists.forEach((list) => {
      const rows = this.rowTargets.filter((row) => row.parentElement === list);
      rows.sort(comparator).forEach((row) => list.appendChild(row));
    });
  }

  // eslint-disable-next-line class-methods-use-this
  comparator(mode) {
    const price = (row) => parseInt(row.dataset.priceCents || "0", 10);
    const name = (row) => row.dataset.name || "";
    const original = (row) => parseInt(row.dataset.originalIndex || "0", 10);

    switch (mode) {
      case "name":
        return (a, b) => name(a).localeCompare(name(b));
      case "price_asc":
        return (a, b) => price(a) - price(b) || name(a).localeCompare(name(b));
      case "price_desc":
        return (a, b) => price(b) - price(a) || name(a).localeCompare(name(b));
      default:
        return (a, b) => original(a) - original(b);
    }
  }
}
